import React, { useEffect, useRef, useState } from 'react'
import '../../App.css'
import '../../Password.css'
import './ResendCode.css'
import { Link, Redirect, useLocation } from 'react-router-dom'
import Axios from 'axios'
import ClipLoader from 'react-spinners/ClipLoader'
import { API_URL } from '../../config'

const COOLDOWN = 30
const baseURL = `${API_URL}/apps/kscanner/webservices.php`

const formatTime = (s) => `0:${String(s).padStart(2, '0')}`

const ResendCode = () => {
  const location = useLocation()
  const state = location.state || {}
  const email = state.email
  const password = state.password
  const account_type = state.accounttype

  const timeoutRef = useRef(null)
  const deadline = useRef(Date.now() + COOLDOWN * 1000)

  const [code, setCode] = useState('')
  const [notice, setNotice] = useState(null) // { text, type: 'error' | 'success' }
  const [verifying, setVerifying] = useState(false)
  const [accountVerified, setAccountVerified] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(COOLDOWN)
  const [resending, setResending] = useState(false)

  const running = secondsLeft > 0

  useEffect(() => {
    document.title = 'Verifying account - Karamale'

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Desktop is a single, scroll-free screen (mobile keeps normal scrolling)
  useEffect(() => {
    const prev = document.body.style.overflow

    if (window.innerWidth > 900) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Deadline-based so background-tab throttling doesn't stretch the countdown
  useEffect(() => {
    if (!running) return

    const id = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000)))
    }, 250)

    return () => clearInterval(id)
  }, [running])

  const clearNotice = () => {
    setNotice(null)
  }

  const showNotice = (text, type = 'error') => {
    setNotice({ text, type })

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setNotice(null)
    }, 5000)
  }

  const handleVerify = async () => {
    clearNotice()

    if (code.trim() === '') {
      showNotice('Enter the verification code')
      return
    }

    setVerifying(true)

    const affiliate_id = localStorage.getItem('affiliateid') ?? null

    const data = new FormData()
    data.append('email', email)
    data.append('password', password)
    data.append('verificationCode', code)
    data.append('account_type', account_type)
    data.append('affiliate_id', affiliate_id)
    data.append('query', 'REGISTRATION')

    try {
      const response = await Axios.post(baseURL, data, { withCredentials: true })

      if (response.data.status === 'success') {
        localStorage.removeItem('affiliateid')
        setAccountVerified(true)
        return
      }

      showNotice(response.data.message || 'That code is not valid. Please try again.')
    } catch (err) {
      console.log(err)
      showNotice('Something went wrong. Please try again.')
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    if (resending) return

    clearNotice()
    setResending(true)

    const data = new FormData()
    data.append('email', email)
    data.append('query', 'RESEND_VERIFICATION_CODE')

    try {
      const res = await Axios.post(baseURL, data, { withCredentials: true })

      if (res.data.status === 'success') {
        deadline.current = Date.now() + COOLDOWN * 1000
        setSecondsLeft(COOLDOWN)
        showNotice(res.data.message || 'A new code is on its way.', 'success')
        return
      }

      showNotice(res.data.message || "We couldn't send the code. Please try again.")
    } catch (err) {
      console.log(err)
      showNotice("We couldn't send the code. Please try again.")
    } finally {
      setResending(false)
    }
  }

  if (!localStorage.getItem('verificationAccount')) {
    return <Redirect to="/register" />
  }

  return (
    <div className="kx-password-page kx-verify">
      <div className="kx-password-bg">
        <div className="kx-password-bg__orb kx-password-bg__orb--one"></div>
        <div className="kx-password-bg__orb kx-password-bg__orb--two"></div>
        <div className="kx-password-bg__grid"></div>
        <div className="kx-password-bg__word">KARAMALE</div>
      </div>

      <main className="kx-password">
        <div className="kx-password__wrap">
          <section className="kx-password__brand">
            <Link to="/" className="kx-password__logo">
              karamale.
            </Link>

            <div className="kx-password__brand-content">
              <div className="kx-password__eyebrow">EMAIL VERIFICATION</div>

              {accountVerified ? (
                <>
                  <h1>
                    Welcome to
                    <strong>Karamale.</strong>
                  </h1>

                  <p>Your email is confirmed and your account is active. Log in to get started.</p>
                </>
              ) : (
                <>
                  <h1>
                    Check your
                    <strong>inbox.</strong>
                  </h1>

                  <p>
                    We've sent a verification code to the email address you signed up with. Enter it
                    to activate your Karamale account.
                  </p>
                </>
              )}

              <div className="kx-password__features">
                <div className="kx-password__feature">
                  <span className="kx-password__feature-mark">✓</span>
                  <span>Create your account</span>
                </div>

                <div className="kx-password__feature">
                  <span className="kx-password__feature-mark">{accountVerified ? '✓' : '02'}</span>
                  <span>Confirm your email address</span>
                </div>

                <div className="kx-password__feature">
                  <span className="kx-password__feature-mark">03</span>
                  <span>Start using Karamale</span>
                </div>
              </div>
            </div>

            <div className="kx-password__brand-footer">
              <span>Wrong email address?</span>
              <Link to="/register">Back to sign up</Link>
            </div>
          </section>

          <section className="kx-password__panel">
            <div className="kx-password__card">
              {accountVerified ? (
                <>
                  <div className="kx-password__card-head">
                    <span className="kx-password__card-kicker">ACCOUNT VERIFIED</span>

                    <h2>You're all set.</h2>

                    <p>Your account is verified and ready to go.</p>
                  </div>

                  <div className="kx-password__form">
                    <a href={`${API_URL}/login`} className="kx-password__submit">
                      <span>Proceed to log in</span>
                      <span className="kx-password__submit-arrow">→</span>
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <div className="kx-password__card-head">
                    <span className="kx-password__card-kicker">CONFIRM EMAIL</span>

                    <h2>Confirm your email.</h2>

                    <p>
                      We've sent a verification code to{' '}
                      <strong style={{ color: '#111111' }}>{email}</strong>
                    </p>
                  </div>

                  <div className="kx-password__form">
                    <div className="kx-password__field">
                      <label>Verification code</label>

                      <input
                        type="text"
                        name="code"
                        required
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="Enter verification code"
                        value={code}
                        onChange={(e) => {
                          setCode(e.target.value)
                          clearNotice()
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !verifying) {
                            handleVerify()
                          }
                        }}
                      />
                    </div>

                    {notice && (
                      <div
                        className={
                          notice.type === 'success' ? 'kx-password__success' : 'kx-password__error'
                        }
                        role={notice.type === 'success' ? 'status' : 'alert'}
                      >
                        {notice.text}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleVerify}
                      className="kx-password__submit"
                      disabled={verifying}
                    >
                      {verifying ? (
                        <ClipLoader color="#ffffff" loading={true} size={20} />
                      ) : (
                        <>
                          <span>Verify</span>
                          <span className="kx-password__submit-arrow">→</span>
                        </>
                      )}
                    </button>

                    <div className="kx-password__resend">
                      {running ? (
                        <div className="kx-password__resend-wait">
                          Didn't get a code? Request a new one in{' '}
                          <strong>{formatTime(secondsLeft)}</strong>
                          <div className="kx-password__resend-bar" aria-hidden="true">
                            <span style={{ width: `${(secondsLeft / COOLDOWN) * 100}%` }}></span>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResend}
                          className="kx-password__resend-btn"
                          disabled={resending}
                        >
                          {resending ? (
                            <ClipLoader color="#e21b2d" loading={true} size={18} />
                          ) : (
                            <>
                              <span>Resend code</span>
                              <span className="kx-password__submit-arrow">→</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    <Link to="/" className="kx-password__back">
                      ← Back to Karamale
                    </Link>
                  </div>
                </>
              )}
            </div>

            {!accountVerified && (
              <div className="kx-password__notice">
                Check your spam folder if the code doesn't arrive.
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}

export default ResendCode
